import React, { useState } from 'react';
import { RiDatabase2Fill } from 'react-icons/ri';
import { MdOutlineArrowRightAlt, MdCompareArrows, MdFiberManualRecord } from 'react-icons/md';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import CircularProgress from '@mui/material/CircularProgress';
import { Select } from '@welcome-ui/select';

import { getMainStatus, getBackupStatus, createBackup, getDbSynchroStatus, synchroDB, stopReplication } from '../../api/admin';
import { useNotification } from '../../context/notification';

function Database() {
  const [selectOptions] = useState([]);
  const [value, setValue] = useState(null);
  const [valueAntenne, setValueAntenne] = useState(null);
  const queryClient = useQueryClient();
  const { addNotification } = useNotification();

  const { data: mainStatus, isLoading: mainIsLoading } = useQuery('mainDB', getMainStatus, {
    onSuccess: (data) => {
      if (data.connected) {
        selectOptions[0] = { label: `Exporter les données de la base ${data.ip}`, value: data.ip };
      }
    },
    refetchInterval: 60 * 1000,
  });

  const { data: backupStatus, isLoading: backupIsLoading } = useQuery('backupDB', getBackupStatus, {
    onSuccess: (data) => {
      if (data.connected) {
        selectOptions[1] = { label: `Importer les données de la base ${data.ip}`, value: data.ip };
      }
    },
    refetchInterval: 60 * 1000,
  });

  const { data: synchroStatus, isLoading: synchroStatusIsLoading } = useQuery('synchroDB', getDbSynchroStatus, {
    refetchInterval: 3 * 1000,
  });

  const { mutate: synchro, isLoading: synchroIsLoading } = useMutation((data) => synchroDB(data), {
    onSuccess: (data) => {
      queryClient.invalidateQueries('synchroDB');
      addNotification({ message: data.message });
    },
    onError: () => {
      addNotification({ message: 'Une erreur est survenue lors de la synchronisation.', severity: 'error' });
    },
  });

  const { mutate: backup } = useMutation((data) => createBackup(data), {
    onSuccess: (data) => {
      addNotification({ message: data.message });
      synchro({ ip: data.ip_host, db_name: data.db_name });
    },
    onError: () => {
      addNotification({ message: 'Une erreur est survenue lors de la création du fichier backup.', severity: 'error' });
    },
  });

  const { mutate: stopRep } = useMutation(() => stopReplication(), {
    onSuccess: (data) => {
      addNotification({ message: data.message });
    },
    onError: () => {
      addNotification({ message: 'Une erreur est survenue lors de la création du fichier backup.', severity: 'error' });
    },
  });

  return (
    <section className="grid grid-cols-[60%_40%] database">
      <div className="grid grid-rows-2">
        <div className="flex justify-center items-center top-10 space-x-6">
          <div
            style={{ width: '450px' }}
            className={`bg-black p-4 rounded flex flex-col justify-center h-52 row-start-1 ${
              mainStatus?.connected ? 'opacity-100' : 'opacity-50'
            }`}
          >
            <div className="space-y-2">
              <h2 className="font-bold">DATABASE TICKER</h2>
              <h2 className="">{`${mainStatus.name}`}</h2>
              <div className="flex items-center space-x-2">
                <RiDatabase2Fill size={20} />
                {mainIsLoading ? (
                  <CircularProgress size={20} />
                ) : (
                  <>
                    <div className="flex items-center" title={mainStatus?.message}>
                      <MdFiberManualRecord
                        className={backupStatus?.connected && 'glow'}
                        color={backupStatus?.connected ? 'var(--on)' : 'var(--off)'}
                      />
                    </div>
                    <h2 className="">{`${mainStatus.ip}`}</h2>
                  </>
                )}
              </div>
            </div>

            <Select
              disabled={!mainStatus?.connected || !backupStatus?.connected}
              name="selectTemplate"
              className="mt-2 mb-1 max-w-md"
              placeholder="Choisir une option..."
              size="sm"
              options={
                mainIsLoading || backupIsLoading ? [{ label: 'Chargement...', value: '', icon: CircularProgress }] : selectOptions
              }
              onChange={(e) => setValue(e)}
              value={value}
            />
            {(!mainStatus?.connected || !backupStatus?.connected) && (
              <span className="text-red-400 text-xs">Une ou plusieurs databases déconnectées</span>
            )}
            <button
              disabled={!mainStatus?.connected || !backupStatus?.connected}
              type="button"
              className="mt-6 w-fit bg-zinc-800 py-2 px-4 rounded disabled:opacity-50 hover:bg-blue-400 disabled:hover:bg-zinc-800 duration-300"
              onClick={() => backup({ ip: value, db_name: "ticker" })}
            >
              Valider
            </button>
          </div>
          {value === mainStatus?.ip && <MdOutlineArrowRightAlt size={60} />}
          {value === backupStatus?.ip && <MdOutlineArrowRightAlt className="rotate-180" size={60} />}
          <div
            className={`bg-black p-4 rounded flex flex-col items-center justify-center space-y-4 w-52 h-52 ${
              backupStatus?.connected ? 'opacity-100' : 'opacity-50 text-red-400'
            }`}
          >
            <h2 className="">{`${backupStatus?.name}`}</h2>
            <div className="flex items-center space-x-2">
              <RiDatabase2Fill size={20} />
              {backupIsLoading ? (
                <CircularProgress size={20} />
              ) : (
                <>
                  <div className="flex items-center" title={mainStatus?.message}>
                    <MdFiberManualRecord
                      className={backupStatus?.connected && 'glow'}
                      color={backupStatus?.connected ? 'var(--on)' : 'var(--off)'}
                    />
                  </div>
                  <h2 className="">{`${backupStatus?.ip}`}</h2>
                </>
              )}
            </div>
          </div>
        </div>

        <div className="flex justify-center items-center space-x-6"> 
          <div
            style={{ width: '450px' }}
            className={`bg-black p-4 rounded flex flex-col justify-center h-52 ${
              mainStatus?.connected ? 'opacity-100' : 'opacity-50'
            }`}
          >
            <div className="space-y-2">
              <h2 className="font-bold">DATABASE ANTENNE_DB</h2>
              <h2 className="">{`${mainStatus.name}`}</h2>
              <div className="flex items-center space-x-2">
                <RiDatabase2Fill size={20} />
                {mainIsLoading ? (
                  <CircularProgress size={20} />
                ) : (
                  <>
                    <div className="flex items-center" title={mainStatus?.message}>
                      <MdFiberManualRecord
                        className={backupStatus?.connected && 'glow'}
                        color={backupStatus?.connected ? 'var(--on)' : 'var(--off)'}
                      />
                    </div>
                    <h2 className="">{`${mainStatus.ip}`}</h2>
                  </>
                )}
              </div>
            </div>

            <Select
              disabled={!mainStatus?.connected || !backupStatus?.connected}
              name="selectTemplate"
              className="mt-2 mb-1 max-w-md"
              placeholder="Choisir une option..."
              size="sm"
              options={
                mainIsLoading || backupIsLoading ? [{ label: 'Chargement...', value: '', icon: CircularProgress }] : selectOptions
              }
              onChange={(e) => setValueAntenne(e)}
              value={valueAntenne}
            />
            {(!mainStatus?.connected || !backupStatus?.connected) && (
              <span className="text-red-400 text-xs">Une ou plusieurs databases déconnectées</span>
            )}
            <button
              disabled={!mainStatus?.connected || !backupStatus?.connected}
              type="button"
              className="mt-6 w-fit bg-zinc-800 py-2 px-4 rounded disabled:opacity-50 hover:bg-blue-400 disabled:hover:bg-zinc-800 duration-300"
              onClick={() => backup({ ip: valueAntenne, db_name: "antenne_db" })}
            >
              Valider
            </button>
          </div>
          {valueAntenne === mainStatus?.ip && <MdOutlineArrowRightAlt size={60} />}
          {valueAntenne === backupStatus?.ip && <MdOutlineArrowRightAlt className="rotate-180" size={60} />}
          <div
            className={`bg-black p-4 rounded flex flex-col items-center justify-center space-y-4 w-52 h-52 ${
              backupStatus?.connected ? 'opacity-100' : 'opacity-50 text-red-400'
            }`}
          >
            <h2 className="">{`${backupStatus?.name}`}</h2>
            <div className="flex items-center space-x-2">
              <RiDatabase2Fill size={20} />
              {backupIsLoading ? (
                <CircularProgress size={20} />
              ) : (
                <>
                  <div className="flex items-center" title={mainStatus?.message}>
                    <MdFiberManualRecord
                      className={backupStatus?.connected && 'glow'}
                      color={backupStatus?.connected ? 'var(--on)' : 'var(--off)'}
                    />
                  </div>
                  <h2 className="">{`${backupStatus?.ip}`}</h2>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="flex col-start-2 justify-left items-center space-x-6"> 
        <div className="bg-black p-4 rounded flex flex-col items-center justify-center space-y-2 w-fit h-42">
          <div className=" flex items-center space-x-2">
            <MdCompareArrows size={25} />
            <h2 className="text-center">BDD Réplication</h2>
          </div>

          {synchroStatusIsLoading || synchroIsLoading ? (
            <CircularProgress size={20} />
          ) : (
            <span className={`${synchroStatus.synchro ? 'text-green-400' : 'text-red-400'}`}>
              {synchroStatus.synchro ? 'Active' : 'Désactivée'}
            </span>
          )}

          <button
            disabled={!synchroStatus.synchro}
            type="button"
            className="mt-6 w-fit bg-zinc-800 py-2 px-4 rounded disabled:opacity-50 hover:bg-blue-400 disabled:hover:bg-zinc-800 duration-300"
            onClick={() => stopRep()}
          >
            Stop
          </button>
        </div>
      </div>

    </section>
  );
}

Database.propTypes = {};

export default Database;
